// src/Screens/Components/FileUpload.tsx

import { cn } from "../../utils/cn";
import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { IconUpload, IconX } from "@tabler/icons-react";
import { useDropzone } from "react-dropzone";

const mainVariant = {
  initial: { x: 0, y: 0 },
  animate: { x: 20, y: -20, opacity: 0.9 },
};

const secondaryVariant = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
};

export const FileUpload = ({
  onChange,
  initialFile,
  fieldName,
}: {
  onChange?: (files: File[]) => void;
  initialFile?: string;
  fieldName: string;
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previousFile, setPreviousFile] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialFile) {
      setPreviousFile(initialFile);
    }
  }, [initialFile]);

  const handleFileChange = (newFiles: File[]) => {
    setFiles(newFiles);
    setPreviousFile(null);
    onChange && onChange(newFiles);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setFiles([]);
    setPreviousFile(null);
    onChange && onChange([]);
  };

  const { getRootProps, isDragActive } = useDropzone({
    multiple: false,
    noClick: true,
    onDrop: handleFileChange,
    onDropRejected: (error) => {
      console.log(error);
    },
  });

  const renderFileInfo = (file: File | string) => {
    const isFileObject = file instanceof File;
    const fileName = isFileObject ? file.name : file.split('/').pop() || 'Unknown file';
    const fileSize = isFileObject ? (file.size / (1024 * 1024)).toFixed(2) + ' MB' : 'Unknown size';
    const fileType = isFileObject ? file.type : 'Unknown type';
    const modifiedDate = isFileObject ? new Date(file.lastModified).toLocaleDateString() : 'Unknown date';

    return (
      <motion.div
        layoutId={`file-upload-${fieldName}`}
        className="relative overflow-hidden z-40 bg-white flex flex-col items-start justify-start md:h-24 p-4 mt-4 w-full mx-auto rounded-md shadow-sm"
      >
        <div className="flex justify-between w-full items-center gap-4">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            layout
            className="text-base text-gray-700 truncate max-w-xs"
          >
            {fileName}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            layout
            className="rounded-lg px-2 py-1 w-fit flex-shrink-0 text-sm text-gray-600 shadow-input"
          >
            {fileSize}
          </motion.p>
        </div>

        <div className="flex text-sm md:flex-row flex-col items-start md:items-center w-full mt-2 justify-between text-gray-600">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            layout
            className="px-1 py-0.5 rounded-md bg-gray-100"
          >
            {fileType}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            layout
          >
            Modified on {modifiedDate}
          </motion.p>
        </div>
        <button
          onClick={handleRemoveFile}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          <IconX size={18} />
        </button>
      </motion.div>
    );
  };

  return (
    <div className="w-full" {...getRootProps()}>
      <motion.div
        onClick={handleClick}
        whileHover="animate"
        className="p-6 group/file block rounded-lg cursor-pointer w-full relative overflow-hidden"
      >
        <input
          ref={fileInputRef}
          id={`file-upload-handle-${fieldName}`}
          type="file"
          onChange={(e) => handleFileChange(Array.from(e.target.files || []))}
          className="hidden"
        />
        <div className="absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,white,transparent)]">
          <GridPattern />
        </div>
        <div className="flex flex-col items-center justify-center">
          <p className="relative z-20 font-sans font-bold text-gray-700 text-base">
            Upload File
          </p>
          <p className="relative z-20 font-sans font-normal text-gray-500 text-base mt-2">
            Drag or drop your files here or click to upload
          </p>
          <div className="relative w-full mt-6 max-w-xl mx-auto">
            {files.length > 0 && renderFileInfo(files[0])}
            {previousFile && !files.length && renderFileInfo(previousFile)}
            {!files.length && !previousFile && (
              <motion.div
                layoutId={`file-upload-${fieldName}`}
                variants={mainVariant}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
                className={cn(
                  "relative group-hover/file:shadow-2xl z-40 bg-white flex items-center justify-center h-32 mt-4 w-full max-w-[8rem] mx-auto rounded-md",
                  "shadow-md"
                )}
              >
                {isDragActive ? (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-gray-600 flex flex-col items-center"
                  >
                    Drop it
                    <IconUpload className="h-6 w-6 text-gray-600 mt-2" />
                  </motion.p>
                ) : (
                  <IconUpload className="h-8 w-8 text-gray-600" />
                )}
              </motion.div>
            )}

            {!files.length && !previousFile && (
              <motion.div
                variants={secondaryVariant}
                className="absolute opacity-0 border border-dashed border-blue-400 inset-0 z-30 bg-transparent flex items-center justify-center h-32 mt-4 w-full max-w-[8rem] mx-auto rounded-md"
              ></motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export function GridPattern() {
  const columns = 41;
  const rows = 11;
  return (
    <div className="flex bg-gray-50 flex-shrink-0 flex-wrap justify-center items-center gap-x-px gap-y-px scale-105">
      {Array.from({ length: rows }).map((_, row) =>
        Array.from({ length: columns }).map((_, col) => {
          const index = row * columns + col;
          return (
            <div
              key={`${col}-${row}`}
              className={`w-10 h-10 flex flex-shrink-0 rounded-[2px] ${
                index % 2 === 0 ? "bg-gray-100" : "bg-gray-200"
              }`}
            />
          );
        })
      )}
    </div>
  );
}
